// React's side of `reviewed`, for containers: the hook takes the plugin's
// `getApi` and binds to the one record the provider registers.
import * as React from 'react';
import { REVIEWED_API, type Reviewed } from './index.ts';

type GetApi = <T>(name: string) => Promise<T>;

/**
 * Whether a game was reviewed, as a function of its id: undefined until the
 * provider has registered and the record is read (so nothing is drawn from a
 * record not read yet), then a function that is stable between changes and
 * replaced on each one, so a component using it re-renders.
 */
export function useReviewed(getApi: GetApi): ((gameId: string) => boolean) | undefined {
  const [reviewed, setReviewed] = React.useState<Reviewed>();
  const [revision, setRevision] = React.useState(0);
  React.useEffect(() => {
    let live = true;
    let unsubscribe: (() => void) | undefined;
    getApi<Reviewed>(REVIEWED_API)
      .then(async (r) => {
        await r.ready();
        if (!live) return;
        unsubscribe = r.subscribe(() => setRevision((n) => n + 1));
        setReviewed(r);
      })
      // A record that cannot be read stays undefined: nothing is drawn from it (the provider logs why).
      .catch(() => undefined);
    return () => {
      live = false;
      unsubscribe?.();
    };
  }, [getApi]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return React.useMemo(() => (reviewed ? (gameId: string) => reviewed.has(gameId) : undefined), [reviewed, revision]);
}
